'use client';

import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import type { CliplisterFragmentFragmentDoc } from '@packages/gql/generated/shopping/CliplisterFragmentFragmentDoc';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { unmask } from '@packages/gql/src/betterMasking';
import { CliplisterVideo } from './CliplisterVideo/CliplisterVideo.client';
import { ImageGalleryCallToActionButton } from '../ImageGalleryCallToActionButton/ImageGalleryCallToActionButton.client';
import { addCliplisterScript } from './addCliplisterScript/addCliplisterScript';
import { useVideoTracking } from '../Tracking/hooks/useVideoTracking';

/* GraphQL */ `
  fragment CliplisterFragment on Product {
    orderNumber
    cliplister(lang: $lang) @skip(if: $skipCliplister) {
      url
    }
  }
`;

export type CliplisterProps = {
  data: FragmentType<typeof CliplisterFragmentFragmentDoc>;
};

/**
 * <p>
 * This component is used to show product videos from cliplister.
 * </p>
 * <p>
 * It originally shows an icon. After it is clicked a `Dialog` opens, which shows a product preview image.
 * After the image is clicked, the player is appended and starts playing the product video.
 *
 * This components needs to have Cookie Consent 4 (marketing) in order to be displayed.
 * <a href="/story/docs-storybooksetup--page">Here</a> you can see how you can simulate the consent in storybook.
 * </p>
 *
 */

export const Cliplister = ({ data }: CliplisterProps) => {
  const { orderNumber, cliplister } = unmask<typeof CliplisterFragmentFragmentDoc>(data);
  const dispatchGTMEvent = useTracking();
  const {
    product: {
      cliplister: { scriptUrl, consentGroup, enabled },
    },
  } = useConfig();
  // scoped two-way rendering to avoid hydration errors because of consent cookie dependecy
  const [rendered, setRendered] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [scriptLoaded, setScriptloaded] = useState(false);

  const isCookieAllowed = useCookieConsent(consentGroup);

  const { formatMessage } = useIntl();
  const { trackGlycerinDisplayVideo, trackVideoIconClick } = useVideoTracking();

  const isCliplisterAllowed = rendered && enabled && !!cliplister?.url && isCookieAllowed;
  const isCliplisterAvailable = isCliplisterAllowed && scriptLoaded;

  useEffect(() => {
    setRendered(true);
  }, []);

  useEffect(() => {
    if (isCliplisterAllowed) {
      addCliplisterScript(scriptUrl)
        .then(() => setScriptloaded(true))
        .catch(() => setScriptloaded(false));
    }
  }, [isCliplisterAllowed, scriptUrl]);

  useEffect(() => {
    if (isCliplisterAvailable) {
      trackGlycerinDisplayVideo();
    }
  }, [isCliplisterAvailable, orderNumber, trackGlycerinDisplayVideo]);

  if (!isCliplisterAvailable) return null;

  const dialogTitle = formatMessage({
    id: 'cliplister.modal.title',
    defaultMessage: 'Produkt Video',
  });

  const openModalHandler = () => {
    trackVideoIconClick();
    setOpenDialog(true);
    dispatchDisplayOverlayTracking({
      eventDispatcher: dispatchGTMEvent,
      displayTrackingProps: {
        category: 'cliplister-dialog',
        label: dialogTitle,
      },
    });
  };

  return (
    <>
      <ImageGalleryCallToActionButton
        onClick={openModalHandler}
        buttonAriaLabel={formatMessage({
          id: 'cliplister.aria.button',
          defaultMessage: 'Produktvideo abspielen',
        })}
      >
        <span className="icon-emp-video text-color-text-dark size-4" />
      </ImageGalleryCallToActionButton>
      <Dialog
        className="w-full max-w-[600px]"
        isOpen={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
      >
        <DialogHeader onClose={() => setOpenDialog(false)}>{dialogTitle}</DialogHeader>
        <DialogContent>
          <CliplisterVideo orderNumber={orderNumber} />
        </DialogContent>
      </Dialog>
    </>
  );
};
